import React, { Component } from 'react';
import { func, object, number, array } from 'prop-types';
import { ModuleRegistry } from 'react-module-container';
import { translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import Widget from '../widget';

import { isAppInstalled } from '@wix/business-manager-api';
import {
  Row,
  Container,
  Page,
  Breadcrumbs,
  Box,
  Loader,
} from 'wix-style-react';
import { isPartner } from '../../utils/data';
import styles from '../widgetObjectsDashboard/styles.scss';

const WidgetsRow = ({ widgets, numberOfWidgets }) => {
  const span = numberOfWidgets && Math.floor(12 / numberOfWidgets);
  return (
    <Row>
      {widgets.map((widget, index) => {
        return <Widget type={widget} key={widget + index} span={span} />;
      })}
    </Row>
  );
};

WidgetsRow.propTypes = {
  widgets: array.isRequired,
  numberOfWidgets: number.isRequired,
};

class Dashboard extends Component {
  static propTypes = {
    appConfig: object,
    biLogger: object,
    embeddedIntegrations: object,
    view: object,
    t: func,
    experiments: object,
  };

  APP_SLUG = 'marketing-integrations';
  PP_ORIGIN = 'marketing-integrations-dashboard';

  state = {
    widgets: [],
  };

  async componentDidMount() {
    this.props.biLogger.integrationsHomePageView();
    this.setState({ widgets: await this.getWidgets() });
  }

  getWidgets = async () => {
    const {
      appConfig: { experiments, widgets, userId, metaSiteId },
      embeddedIntegrations: { getIntegrationStatus },
    } = this.props;

    const isPartnerSite =
      experiments['specs.promote.mi.ui.showMailChimpToPartners'] === 'true' &&
      (await isPartner({ userId, metaSiteId }));
    const isMailChimpInstalled = isAppInstalled(
      '151e32e8-3b14-ab49-6472-11b7d0510427',
    );

    const isGoogleAdsInstalled =
      getIntegrationStatus('googleAdWords') !== 'new';

    return widgets.filter(
      (widget) =>
        !(
          {
            vwo: experiments['specs.promote.mi.ui.RemoveVWO'] !== 'true',
            mailChimp: !!(isPartnerSite || isMailChimpInstalled),
            gtag: !isGoogleAdsInstalled,
            googleAdWords: !!isGoogleAdsInstalled,
          }[widget] === false
        ),
    );
  };

  render() {
    const {
      t,
      appConfig: { experiments },
      view: { maxWidth },
    } = this.props;
    const { widgets } = this.state;

    const isAscendAwarenessEnabled =
      experiments['specs.ascend.AscendAwareness'] === 'true';

    const AscendUpgradeButton =
      isAscendAwarenessEnabled &&
      ModuleRegistry.component('ascend-upgrade-button-lazy-component');
    const AscendBadge =
      isAscendAwarenessEnabled &&
      ModuleRegistry.component('ascend-badge-lazy-component');

    const isAscendUpgradeSpecificContent =
      experiments['specs.promote.mi.AscendUpgradeContent'] === 'true';

    const numberOfWidgets = 3;
    return (
      <Page maxWidth={maxWidth} dataHook="dashboard-main">
        <Page.Header
          title={t('dashboard.title')}
          subtitle={t('dashboard.header.subtitle')}
          hasBackgroundImage
          breadcrumbs={
            isAscendAwarenessEnabled &&
            ((minimized) => (
              <Breadcrumbs
                activeId="1"
                items={[
                  {
                    id: '1',
                    value: (
                      <AscendBadge
                        slug={this.APP_SLUG}
                        title={t('dashboard.title')}
                        minimized={minimized}
                      />
                    ),
                  },
                ]}
              />
            ))
          }
          actionsBar={
            isAscendAwarenessEnabled && (
              <AscendUpgradeButton
                slug={this.APP_SLUG}
                origin={this.PP_ORIGIN}
                items={Array.from({
                  length: isAscendUpgradeSpecificContent ? 3 : 4,
                }).map((_, i) =>
                  t(
                    `${
                      isAscendUpgradeSpecificContent
                        ? 'mi'
                        : 'marketing-integration'
                    }.ascend-awareness.button.item-${i + 1}`,
                  ),
                )}
              />
            )
          }
        />
        <Page.Content dataHook="market-integration-inhouse">
          {widgets.length === 0 ? (
            <div className={styles.loaderContainer}>
              <div className={styles.loader}>
                <Loader data-hook="dashboard-page-loader" />
              </div>
            </div>
          ) : (
            <Box paddingBottom="50px">
              <Container>
                {widgets.map((widget, index) => { // eslint-disable-line
                  if (index % numberOfWidgets === 0) {
                    const rowWidgets = widgets.slice(
                      index,
                      index + numberOfWidgets,
                    );
                    return (
                      <WidgetsRow
                        key={index}
                        widgets={rowWidgets}
                        numberOfWidgets={numberOfWidgets}
                      />
                    );
                  }
                })}
              </Container>
            </Box>
          )}
        </Page.Content>
      </Page>
    );
  }
}

export default translate()(
  inject(
    'appConfig',
    'biLogger',
    'view',
    'embeddedIntegrations',
  )(observer(Dashboard)),
);
